import { IObservableArray } from "mobx";
import { CollectionName, Genome, GenomeInList, PaginationMetadata } from "../api";
import type { DownloadInfo } from "../components/pages/genomes/download-selected-button";

export type GenomesPageData = {
  genomes: IObservableArray<GenomeInList>;
  loading: boolean;
  pagination: PaginationMetadata;
  error: boolean;
};

export function constructGenomeDisplayName(
  genome: GenomeInList,
  collectionName: CollectionName | null
): string {
  // There is divergent logic associated with the display of taxonomic names for bacteria vs. viruses
  // We always want to display bacterial species name, but viral species classifications are too nebulous
  // In the event of a virus, we should display the taxon name even if it is a child of the species

  const speciesTaxon = genome.species_taxon;

  if (collectionName && collectionName === "virology" && genome.taxon_name) {
    return cleanTaxaName(genome.taxon_name);
  } else if (speciesTaxon) {
    return cleanTaxaName(genome.taxon_name);
  }

  return "";
}

export function cleanTaxaName(name: string | undefined | null): string {
  if (name == null) {
    return "";
  }
  return name.replace("[", "").replace("]", "");
}

export function genomeToDownloadInfos(genome: Genome): DownloadInfo[] {
  const downloadInfos: DownloadInfo[] = [...genome.datasets];
  const assembly = genome.primary_assembly;
  const annotationSet = genome.primary_assembly?.primary_annotation_set || null;
  if (assembly) {
    let filename = assembly.filename;
    if (filename.endsWith(".gz")) {
      filename = filename.slice(0, -3);
    }
    downloadInfos.push({
      type: "assembly",
      created_at: assembly.created_at,
      filename: filename,
      size: assembly.size,
    });
  }
  if (annotationSet) {
    let filename = annotationSet.filename;
    if (filename.endsWith(".gz")) {
      filename = filename.slice(0, -3);
    }
    downloadInfos.push({
      type: "annotations",
      created_at: annotationSet.created_at,
      filename: filename,
      size: annotationSet.size,
    });
  }
  return downloadInfos;
}

export function catalogItemDisplayName(name: string, catalogNumber: string | null): string {
  if (name === "atcc-" + (catalogNumber ?? "")) {
    return "ATCC® " + catalogNumber + "™";
  }
  return name;
}

export const getGenomeSeo = (genome: Genome) => {
  return {
    titleTemplate: `%s | ATCC Genome Portal`,
    title: constructGenomeName(genome),
    description: constructGenomeDescription(genome),
  };
};

export function getQCValue(
  values: undefined | string[] | number[],
  index: number
): number | string {
  if (!values || index >= values.length) {
    return "-";
  }

  return values[index] ?? 0;
}

const constructGenomeName = (genome: Genome) => {
  return `${genome.name} | ${genome.taxon_name}`;
};

const constructGenomeDescription = (genome: Genome) => {
  const genusParent = genome.taxon.parents.filter((parent) => parent.rank === "genus")[0];
  return `${genome.taxon_name} is a strain of ${
    genome.collection_name
      ? colloquialNameFromCollectionName(genome.collection_name as CollectionName)
      : "bacteria"
  }${
    genusParent ? " in the " + genusParent.name + " genus." : "."
  } This genome was published to the ATCC Genome Portal on ${genome.created_at.split("T")[0]}`;
};

const colloquialNameFromCollectionName = (collectionName: CollectionName) => {
  if (collectionName === "virology") {
    return "virus";
  }

  if (collectionName === "bacteriology") {
    return "bacteria";
  }

  return "";
};
